// Generic SSE composable.
//
// Wraps `EventSource` with lifecycle cleanup, auto-reconnect on transient
// network drops, and a typed callback for each event. The event payload
// shape is project-specific — callers pass `T` as the discriminated union
// they expect (for example, `NotificationStreamEvent`). Platform does not
// expose an extraction-run stream; extraction results arrive through /v1.
//
// Usage:
//
//   const { open, close } = useSseStream<NotificationStreamEvent>({
//     onEvent(event) { /* narrow by event.type */ },
//     onClose() { /* terminal or unmount */ },
//   })
//   open('/notifications/events')
//
// Closes automatically on component unmount.

import { onUnmounted } from 'vue'

import { getEventsBaseUrl } from '@/api/env'

export interface SseStreamOptions<T> {
  onEvent: (event: T) => void
  onError?: (error: Event) => void
  onOpen?: () => void
  onClose?: () => void
}

const MAX_RECONNECT_ATTEMPTS = 3
const RECONNECT_DELAY_MS = 2_000

export function useSseStream<T>(options: SseStreamOptions<T>) {
  let source: EventSource | null = null
  let reconnectAttempts = 0
  let closedByCaller = false
  let currentPath: string | null = null

  function connect(path: string) {
    const base = getEventsBaseUrl()
    const url = base ? `${base}${path}` : path
    // EventSource respects cookies on same-origin requests. For cross-origin
    // (different events.* subdomain in prod), the backend's CORS config has
    // to allow credentials AND withCredentials must be set here.
    source = new EventSource(url, { withCredentials: true })

    source.onopen = () => {
      reconnectAttempts = 0
      options.onOpen?.()
    }

    source.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data) as T
        options.onEvent(parsed)
      } catch {
        // Malformed payload — skip and stay connected.
      }
    }

    source.onerror = (err) => {
      options.onError?.(err)
      // EventSource auto-reconnects on transient drops but stops on
      // explicit close. If readyState is CLOSED and we didn't ask for
      // it, try to manually reopen up to MAX_RECONNECT_ATTEMPTS times.
      if (!closedByCaller && source?.readyState === EventSource.CLOSED) {
        source = null
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts += 1
          setTimeout(() => connect(path), RECONNECT_DELAY_MS)
        } else {
          options.onClose?.()
        }
      }
    }
  }

  function open(path: string) {
    close()
    closedByCaller = false
    reconnectAttempts = 0
    currentPath = path
    connect(path)
  }

  function close() {
    closedByCaller = true
    if (source) {
      source.close()
      source = null
    }
  }

  // Revive a stream that died — either because the reconnect budget was
  // exhausted (source nulled, onClose fired) or it was closed out-of-band.
  // No-op while connecting or open. Used by the focus/visibility recovery
  // path so live updates resume when the user returns to a backgrounded tab.
  function reopenIfClosed() {
    if (!currentPath) return
    if (!source || source.readyState === EventSource.CLOSED) {
      open(currentPath)
    }
  }

  onUnmounted(() => {
    close()
  })

  return { open, close, reopenIfClosed }
}
